// Vendor libraries
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { URIComponentCodec } from './uri_component_codec';
import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "@angular/platform-browser";
// TODO once we are confident that all endpoints are using this structured format then we remove this option
export const errorResponseFormatter = (enabled) => {
    if (!enabled) {
        // Just pass the observable through, no interception
        return (source$) => source$;
    }
    return (source$) => source$.pipe(catchError((httpErrorResponse) => {
        let parsedError;
        const { error } = httpErrorResponse;
        if (error && typeof error === 'object') {
            parsedError = {
                status: httpErrorResponse.status,
                errorCode: error.error_code || 'UNKNOWN_ERROR',
                errorMessage: error.error_message || httpErrorResponse.message,
                requestId: error.request_id,
                errors: error.errors,
            };
        }
        else {
            parsedError = {
                status: httpErrorResponse.status,
                errorCode: 'UNKNOWN_ERROR',
                errorMessage: error || httpErrorResponse.message || 'Unknown error',
            };
        }
        return throwError(() => parsedError);
    }));
};
export class AjaxService {
    constructor(httpClient, sanitizer) {
        this.httpClient = httpClient;
        this.sanitizer = sanitizer;
    }
    getDataObserveResponse(url, getParams = {}, additionalHeaders = {}) {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.get(url, { headers, observe: 'response', params: getParams });
    }
    getDataObserveBody(url, getParams = {}, additionalHeaders = {}, enableStructuredError = false) {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .get(url, { headers, observe: 'body', params: getParams })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    getDataObserveText(url, getParams = {}, additionalHeaders = {}) {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.get(url, { headers, observe: 'body', params: getParams, responseType: 'text' });
    }
    getJsonP(url) {
        const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, url) ?? '';
        return this.httpClient.jsonp(sanitizedUrl, 'callback');
    }
    postDataObserveBody(url, postParams, additionalHeaders = {}, enableStructuredError = false) {
        const headers = new HttpHeaders({ ...additionalHeaders, 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .post(url, postParams, { headers, responseType: 'json', observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    postJsonDataObserveResponse(url, postParams) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, postParams, { headers, responseType: 'json', observe: 'response' });
    }
    postJsonDataObserveBody(url, postParams, enableStructuredError = false) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .post(url, postParams, { headers, responseType: 'json', observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    postFormEncodedDataObserveResponse(url, postParams) {
        const serializedParams = new HttpParams({ fromObject: postParams });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, serializedParams, { headers, observe: 'response' });
    }
    postFormEncodedDataObserveBody(url, postParams, enableStructuredError = false) {
        const serializedParams = new HttpParams({ fromObject: postParams, encoder: new URIComponentCodec() });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .post(url, serializedParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    postFormEncodedDataWithBlobObserveResponse(url, postParams) {
        const serializedParams = new HttpParams({ fromObject: postParams, encoder: new URIComponentCodec() });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, serializedParams, { headers, observe: 'response', responseType: 'blob' });
    }
    postFormEncodedDataWithTextObserveResponse(url, postParams) {
        const serializedParams = new HttpParams({ fromObject: postParams });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, serializedParams, { headers, responseType: 'text', observe: 'response' });
    }
    // We need a separate method for responseType blob because generics dont work with blobs
    postJsonDataWithBlobResponseObserveResponse(url, postParams) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient.post(url, postParams, { headers, responseType: 'blob', observe: 'response' });
    }
    // We need a separate method for responseType blob because generics dont work with blobs
    postJsonDataWithBlobResponseObserveBody(url, postParams, enableStructuredError = false) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .post(url, postParams, { headers, responseType: 'blob', observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    postMultipartFormDataWithObserveBody(url, formData, enableStructuredError = false) {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .post(url, formData, { headers, observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    putDataObserveResponse(url, putParams) {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.put(url, putParams, { headers, observe: 'response' });
    }
    putDataObserveBody(url, putParams, enableStructuredError = false) {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .put(url, putParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    putJsonDataObserveBody(url, putParams, enableStructuredError = false) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        });
        return this.httpClient
            .put(url, putParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    patchDataObserveResponse(url, patchParams) {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.patch(url, patchParams, { headers, observe: 'response' });
    }
    patchDataObserveBody(url, patchParams, enableStructuredError = false) {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.httpClient
            .patch(url, patchParams, { headers, observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
    deleteDataObserveResponse(url) {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient.delete(url, { headers, observe: 'response' });
    }
    deleteDataObserveBody(url, enableStructuredError = false) {
        const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });
        return this.httpClient
            .delete(url, { headers, observe: 'body' })
            .pipe(errorResponseFormatter(enableStructuredError));
    }
}
AjaxService.ɵfac = function AjaxService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AjaxService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.DomSanitizer)); };
AjaxService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AjaxService, factory: AjaxService.ɵfac, providedIn: 'root' });
